import type { BetterLightsApi } from '../api/api.ts';
import { CLIENT_SCHEMA } from '../api/client.ts';
import { ApiError, isApiError } from '../api/errors.ts';
import type { Change, ProjectRecord } from '../api/types.ts';
import { sha256Hex } from '../lib/hash.ts';
import { sniffImageMime } from '../lib/mime.ts';
import { blobUri } from '../storage/blobStore.ts';
import { type ProjectFile, readProjectJson, referencedBlobs } from '../storage/record.ts';
import type { SyncStore } from './store.ts';
import type { BlobJob, ProjectSync } from './types.ts';

type Api = Pick<
  BetterLightsApi,
  'pull' | 'push' | 'deleteProject' | 'restoreProject' | 'prepareBlobs' | 'commitBlobs' | 'blobUrl'
>;

export interface LocalBlobs {
  get(sha: string): Promise<Blob | null>;
  put(blob: Blob): Promise<string>;
  has(sha: string): Promise<boolean>;
}

export interface SyncDeps {
  api: Api;
  store: SyncStore;
  blobs: LocalBlobs;
  userId: () => string | null;
  /** PUT to a presigned URL with exactly the given headers. */
  upload: (url: string, headers: Record<string, string>, body: Blob) => Promise<void>;
  download: (url: string) => Promise<Blob>;
  now?: () => number;
  /** A project changed because of the server (pull or adopted push). */
  onRemoteProject?: (file: ProjectFile) => void;
  /** A project disappeared because of the server (tombstone / scope revoked). */
  onRemoteDelete?: (id: string) => void;
  onStateChange?: () => void;
  /** Whether the workspace's plan includes background sync (`sync`); default true. */
  canSync?: (scope: string) => boolean;
}

export interface SyncReport {
  pulled: number;
  pushed: number;
  uploaded: number;
  errors: string[];
}

const PULL_PAGES = 50;
const MAX_BACKOFF = 5 * 60_000;

export function backoffMs(attempts: number): number {
  return Math.min(MAX_BACKOFF, 2_000 * 2 ** Math.max(0, attempts - 1));
}

/** Deterministic Idempotency-Key for a push of one local version on one base revision. */
export function pushKey(id: string, version: number, baseRev: number): string {
  return `push-${id}-${version}-${baseRev}`.slice(0, 200);
}

/**
 * Revision-based sync (docs/research/06 §1, §4; contracts/openapi.yaml):
 * pull a cursor-paginated change feed, push whole docs with `baseRev` (the server 3-way
 * merges), adopt the returned doc only if no local edit happened during the push, upload
 * missing blobs through presigned PUTs (+ commit), download blobs lazily. Never throws
 * into the UI and never blocks editing.
 */
export class SyncEngine {
  updateRequired = false;
  running = false;
  private readonly now: () => number;
  private readonly inflightBlobs = new Map<string, Promise<Blob | null>>();
  /** sha → scope, for lazy downloads of blobs referenced by synced projects. */
  readonly blobScopes = new Map<string, string>();

  constructor(private readonly d: SyncDeps) {
    this.now = d.now ?? Date.now;
  }

  /* ----------------------------------------------------------------- local hooks */

  /** A project was saved locally. Starts tracking it if `scope` is given (new project / backup). */
  async localSaved(id: string, scope?: string): Promise<boolean> {
    const userId = this.d.userId();
    if (!userId) return false;
    const row = await this.d.store.getSync(id);
    if (!row) {
      if (!scope) return false;
      await this.d.store.putSync({ id, userId, scope, baseRev: 0, dirty: true, localVersion: 1 });
      return true;
    }
    if (row.userId !== userId || row.requiresUpdate) return false;
    await this.d.store.putSync({ ...row, dirty: true, localVersion: row.localVersion + 1 });
    return true;
  }

  /** A synced project was deleted locally: tombstone it on the server. */
  async localDeleted(id: string): Promise<void> {
    const row = await this.d.store.getSync(id);
    if (!row) return;
    if (row.baseRev === 0) await this.d.store.deleteSync(id);
    else await this.d.store.putSync({ ...row, pendingDelete: true, dirty: false });
  }

  /* ------------------------------------------------------------------- full sync */

  async syncNow(): Promise<SyncReport> {
    const report: SyncReport = { pulled: 0, pushed: 0, uploaded: 0, errors: [] };
    const userId = this.d.userId();
    if (!userId || this.running) return report;
    this.running = true;
    this.d.onStateChange?.();
    try {
      await this.step(report, () => this.pullAll(userId, report));
      await this.step(report, () => this.pushAll(userId, report));
      await this.step(report, () => this.processBlobs(userId, report));
    } finally {
      this.running = false;
      this.d.onStateChange?.();
    }
    return report;
  }

  private async step(report: SyncReport, fn: () => Promise<void>): Promise<void> {
    try {
      await fn();
    } catch (e) {
      report.errors.push(e instanceof Error ? e.message : String(e));
    }
  }

  /* ------------------------------------------------------------------------ pull */

  async pullAll(userId: string, report?: SyncReport): Promise<void> {
    let cursor = await this.d.store.getCursor(userId);
    for (let page = 0; page < PULL_PAGES; page++) {
      let res: Awaited<ReturnType<Api['pull']>>;
      try {
        res = await this.d.api.pull(cursor);
      } catch (e) {
        if (isApiError(e) && (e.status === 410 || e.code === 'cursor_expired') && cursor !== null) {
          cursor = null; // full resync
          await this.d.store.setCursor(userId, null);
          continue;
        }
        throw e;
      }
      for (const scope of res.scopesRevoked) await this.purgeScope(userId, scope);
      for (const change of res.changes) {
        await this.applyChange(userId, change);
        if (report) report.pulled++;
      }
      cursor = res.cursor;
      await this.d.store.setCursor(userId, cursor);
      if (!res.hasMore) return;
    }
  }

  async applyChange(userId: string, change: Change): Promise<void> {
    if (change.kind === 'catalog') {
      if (change.catalog) await this.d.store.putCatalog(change.id, change.catalog);
      return;
    }
    const local = await this.d.store.getSync(change.id);
    if (local && local.userId !== userId) return;
    const movedTo = (change as Change & { movedTo?: string }).movedTo;
    if (change.deleted && movedTo) {
      // Moved to another workspace: remove the copy only if it still belongs to the source scope; the
      // live record arrives in the destination's feed. Unsynced local edits follow the project.
      if (!local || local.scope !== change.scope) return;
      if (local.dirty) {
        await this.d.store.putSync({ ...local, scope: movedTo });
        return;
      }
      await this.d.store.deleteProject(change.id);
      await this.d.store.deleteSync(change.id);
      return;
    }
    if (change.deleted || !change.project?.doc) {
      if (local?.dirty) {
        await this.d.store.putSync({ ...local, remoteDeleted: true });
        return;
      }
      const file = await this.d.store.getProject(change.id);
      await this.d.store.putTrash({
        id: change.id,
        userId,
        scope: change.scope,
        name: (file?.name as string | undefined) ?? null,
        deletedAt: change.project?.deletedAt ? Date.parse(change.project.deletedAt) : this.now(),
      });
      if (local || file) {
        await this.d.store.deleteProject(change.id);
        await this.d.store.deleteSync(change.id);
        this.d.onRemoteDelete?.(change.id);
      }
      return;
    }
    await this.d.store.deleteTrash(change.id);
    const record = change.project;
    if (local?.dirty || local?.pendingDelete) return; // our next push merges against this
    if (local && record.rev <= local.baseRev && !local.requiresUpdate) return;
    await this.adopt(userId, record, local);
  }

  private async adopt(userId: string, record: ProjectRecord, local: ProjectSync | undefined): Promise<void> {
    const file = readProjectJson(record.doc);
    for (const sha of referencedBlobs(file)) this.blobScopes.set(sha, record.scope);
    await this.d.store.putProject(file);
    await this.d.store.putSync({
      id: record.id,
      userId,
      scope: record.scope,
      baseRev: record.rev,
      dirty: false,
      localVersion: local?.localVersion ?? 0,
      requiresUpdate: record.requiresUpdate || undefined,
      syncedAt: this.now(),
    });
    this.d.onRemoteProject?.(file);
  }

  private async purgeScope(userId: string, scope: string): Promise<void> {
    for (const row of await this.d.store.syncRows(userId)) {
      if (row.scope !== scope) continue;
      await this.d.store.deleteProject(row.id);
      await this.d.store.deleteSync(row.id);
      this.d.onRemoteDelete?.(row.id);
    }
  }

  /* ------------------------------------------------------------------------ push */

  async pushAll(userId: string, report?: SyncReport): Promise<void> {
    if (this.updateRequired) return;
    const rows = (await this.d.store.syncRows(userId)).filter(
      (r) => (r.dirty || r.pendingDelete) && !r.requiresUpdate && !r.remoteDeleted,
    );
    for (const row of rows) {
      try {
        if (row.pendingDelete) await this.pushDelete(row);
        else if (await this.pushOne(row)) report && report.pushed++;
      } catch (e) {
        if (isApiError(e) && (e.status === 426 || e.code === 'upgrade_required')) {
          this.updateRequired = true;
          this.d.onStateChange?.();
          return;
        }
        const cur = await this.d.store.getSync(row.id);
        if (cur) await this.d.store.putSync({ ...cur, lastError: e instanceof Error ? e.message : String(e) });
        if (isApiError(e) && e.isNetwork) throw e; // offline: stop this round
      }
    }
  }

  /** Pushes one project. Returns true when the server copy was adopted. */
  async pushOne(row: ProjectSync): Promise<boolean> {
    const file = await this.d.store.getProject(row.id);
    if (!file) return false;
    const version = row.localVersion;
    const res = await this.d.api.push(
      row.id,
      {
        scope: row.scope,
        baseRev: row.baseRev,
        schemaVersion: CLIENT_SCHEMA,
        doc: file as unknown as Record<string, unknown>,
      },
      pushKey(row.id, version, row.baseRev),
    );
    for (const sha of referencedBlobs(file)) this.blobScopes.set(sha, row.scope);
    // The server finds blob refs anywhere in the doc (photo, thumb, renders, exports).
    const missing = new Set(res.missingBlobs.map((r) => r.replace(/^sha256:/, '')));
    for (const sha of missing) await this.enqueueBlob(row.userId, row.scope, sha);
    const cur = (await this.d.store.getSync(row.id)) ?? row;
    if (res.deleted) {
      await this.d.store.putSync({ ...cur, remoteDeleted: true, lastError: undefined });
      return false;
    }
    if (cur.localVersion !== version) {
      // Edited during the push: keep local edits; the next push merges again (baseRev unchanged).
      await this.d.store.putSync({ ...cur, lastError: undefined });
      return false;
    }
    if (res.record.doc) {
      const merged = readProjectJson(res.record.doc);
      for (const sha of referencedBlobs(merged)) this.blobScopes.set(sha, row.scope);
      await this.d.store.putProject(merged);
      if (res.merged) this.d.onRemoteProject?.(merged);
    }
    await this.d.store.putSync({
      ...cur,
      baseRev: res.record.rev,
      dirty: false,
      lastError: undefined,
      requiresUpdate: res.record.requiresUpdate || undefined,
      syncedAt: this.now(),
    });
    return true;
  }

  private async pushDelete(row: ProjectSync): Promise<void> {
    await this.d.api.deleteProject(row.id, row.baseRev);
    await this.d.store.deleteSync(row.id);
  }

  /** Moves a project to another workspace (POST /v1/projects/{id}/move) and adopts the result. */
  async move(
    id: string,
    toScope: string,
    move: (id: string, toScope: string) => Promise<ProjectRecord>,
  ): Promise<void> {
    const userId = this.d.userId();
    if (!userId) return;
    const record = await move(id, toScope);
    const local = await this.d.store.getSync(id);
    if (local?.dirty) await this.d.store.putSync({ ...local, scope: record.scope, baseRev: record.rev });
    else await this.adopt(userId, record, local);
  }

  /** Undo a server tombstone; the restored doc becomes the local copy (local edits are re-pushed). */
  async restore(id: string): Promise<ProjectFile | null> {
    const userId = this.d.userId();
    if (!userId) return null;
    const record = await this.d.api.restoreProject(id);
    const local = await this.d.store.getSync(id);
    await this.d.store.deleteTrash(id);
    if (local?.remoteDeleted && local.dirty) {
      await this.d.store.putSync({ ...local, remoteDeleted: undefined, baseRev: record.rev });
      return (await this.d.store.getProject(id)) ?? null;
    }
    await this.adopt(userId, record, local);
    return (await this.d.store.getProject(id)) ?? null;
  }

  /* ----------------------------------------------------------------------- blobs */

  async enqueueBlob(userId: string, scope: string, sha: string): Promise<void> {
    const key = `${scope}:${sha}`;
    await this.d.store.putJob({ key, userId, scope, sha, attempts: 0, nextAt: 0 });
  }

  async processBlobs(userId: string, report?: SyncReport): Promise<void> {
    const due = (await this.d.store.jobs(userId)).filter((j) => j.nextAt <= this.now());
    const byScope = new Map<string, BlobJob[]>();
    for (const j of due) byScope.set(j.scope, [...(byScope.get(j.scope) ?? []), j]);
    for (const [scope, jobs] of byScope) {
      for (let i = 0; i < jobs.length; i += 100) await this.uploadBatch(scope, jobs.slice(i, i + 100), report);
    }
  }

  /** Uploads these blobs right away (render inputs). Throws if any is not confirmed. */
  async uploadNow(scope: string, shas: string[], purpose: 'sync' | 'render' = 'sync'): Promise<void> {
    const userId = this.d.userId();
    if (!userId) throw new ApiError(401, null, 'Sign in to continue.');
    for (const sha of shas) await this.enqueueBlob(userId, scope, sha);
    const mine = async () => (await this.d.store.jobs(userId)).filter((j) => j.scope === scope && shas.includes(j.sha));
    await this.uploadBatch(scope, await mine(), undefined, purpose);
    const left = await mine();
    if (left.length) throw new Error(left[0].error ?? 'Upload failed. Check your connection and try again.');
  }

  private async uploadBatch(
    scope: string,
    jobs: BlobJob[],
    report?: SyncReport,
    purpose: 'sync' | 'render' = 'sync',
  ): Promise<void> {
    const blobs = new Map<string, Blob>();
    for (const j of jobs) {
      const b = await this.d.blobs.get(j.sha);
      if (b) blobs.set(j.sha, b);
      else await this.d.store.deleteJob(j.key); // not on this device: nothing to upload
    }
    const live = jobs.filter((j) => blobs.has(j.sha));
    if (live.length === 0) return;
    try {
      const specs = await Promise.all(
        live.map(async (j) => {
          const b = blobs.get(j.sha)!;
          return { ref: blobUri(j.sha), bytes: b.size, mime: await sniffImageMime(b) };
        }),
      );
      const prep = await this.d.api.prepareBlobs(scope, specs, purpose);
      const uploaded: string[] = [];
      for (const u of prep.uploads) {
        const sha = u.ref.replace(/^sha256:/, '');
        const spec = specs.find((x) => x.ref === u.ref);
        const b = blobs.get(sha)!;
        await this.d.upload(u.url, u.headers, spec && b.type !== spec.mime ? new Blob([b], { type: spec.mime }) : b);
        uploaded.push(u.ref);
      }
      const commit = uploaded.length ? await this.d.api.commitBlobs(scope, uploaded) : { present: [], missing: [] };
      const done = new Set([...prep.present, ...commit.present].map((r) => r.replace(/^sha256:/, '')));
      for (const j of live) {
        if (done.has(j.sha)) {
          await this.d.store.deleteJob(j.key);
          if (report) report.uploaded++;
        } else await this.retryLater(j, 'Upload not confirmed');
      }
      await this.setQuotaBlocked(scope, false);
    } catch (e) {
      if (isApiError(e) && (e.status === 402 || e.code === 'storage_quota')) await this.setQuotaBlocked(scope, true);
      for (const j of live) await this.retryLater(j, e instanceof Error ? e.message : String(e));
      if (isApiError(e) && e.isNetwork) throw e;
    }
  }

  private async retryLater(j: BlobJob, error: string): Promise<void> {
    const attempts = j.attempts + 1;
    await this.d.store.putJob({ ...j, attempts, error, nextAt: this.now() + backoffMs(attempts) });
  }

  private async setQuotaBlocked(scope: string, blocked: boolean): Promise<void> {
    const userId = this.d.userId();
    if (!userId) return;
    for (const row of await this.d.store.syncRows(userId)) {
      if (row.scope === scope && !!row.quotaBlocked !== blocked)
        await this.d.store.putSync({ ...row, quotaBlocked: blocked || undefined });
    }
  }

  /** Downloads a blob this device doesn't have yet (presigned GET), verifying its hash. */
  ensureBlob(sha: string, scope?: string): Promise<Blob | null> {
    const running = this.inflightBlobs.get(sha);
    if (running) return running;
    const job = (async () => {
      const local = await this.d.blobs.get(sha);
      if (local) return local;
      const userId = this.d.userId();
      if (!userId) return null;
      if (!scope && !this.blobScopes.has(sha)) await this.indexBlobs(userId);
      const s = scope ?? this.blobScopes.get(sha);
      if (!s) return null;
      try {
        const dl = await this.d.api.blobUrl(blobUri(sha), s);
        const blob = await this.d.download(dl.url);
        if ((await sha256Hex(blob)) !== sha) throw new ApiError(0, null, 'Downloaded file is damaged.');
        await this.d.blobs.put(blob);
        return blob;
      } catch {
        return null;
      }
    })();
    this.inflightBlobs.set(sha, job);
    void job.finally(() => this.inflightBlobs.delete(sha));
    return job;
  }

  /** Rebuild the sha → scope index from tracked projects (after a reload). */
  async indexBlobs(userId: string): Promise<void> {
    for (const row of await this.d.store.syncRows(userId)) {
      const f = await this.d.store.getProject(row.id);
      if (f) for (const sha of referencedBlobs(f)) this.blobScopes.set(sha, row.scope);
    }
  }
}
